// frontend/src/components/JournalEditor/utils/sectionHelpers.js

/**
 * Applicerar ordning från mallen på sektioner
 * @param {Array} sections - Array av sektioner
 * @param {Object} template - Mall med sections array
 * @returns {Array} Sorterade sektioner enligt mallens ordning
 */
export function applyOrderFromTemplate(sections, template) {
  if (!template?.sections) return sections;
  
  const titleToOrder = {};
  for (const tsec of template.sections) {
    titleToOrder[tsec.section_title.trim().toLowerCase()] = parseInt(
      tsec.order,
      10
    );
  }
  
  const adjusted = sections.map((sec) => {
    const key = sec.title?.trim().toLowerCase();
    const foundOrder = titleToOrder[key] ?? 9999;
    return { ...sec, order: foundOrder };
  });
  
  adjusted.sort((a, b) => a.order - b.order);
  return adjusted;
}

/**
 * Tillåter ENBART rubriker som finns i den aktiva journalmallen.
 * Allt annat ("felrubriker") läggs som text i föregående sektion.
 *
 *  – allowedTitles  = mängden rubriker från mallen (småbokstäver, trimmade)
 *  – looksTooLong   = skydd mot att extremt långa rader råkar tolkas som rubrik
 */
export function sanitizeGptSections(rawSections, template) {
  if (!rawSections || rawSections.length === 0) return rawSections;

  // ✔︎ Spara rubrikerna precis som de står i mallen
  const allowedTitles = new Set(
    (template?.sections || []).map((s) => s.section_title.trim())
  );

  const cleaned = [];
  const MAX_TITLE_LEN = 60; // skydd mot extremt långa "rubriker"

  rawSections.forEach((sec) => {
    const title = (sec.title || '').trim();
    const content = (sec.content || '').trim();
    const isAllowed = allowedTitles.has(title);
    const looksTooLong = title.length > MAX_TITLE_LEN;

    if (isAllowed && !looksTooLong) {
      // Giltig rubrik - lägg till
      cleaned.push({ title, content });
    } else if (cleaned.length > 0) {
      // Ogiltig rubrik - lägg som text i föregående sektion
      const textToAdd = [title, content].filter(Boolean).join('\n');
      if (textToAdd) {
        cleaned[cleaned.length - 1].content += '\n' + textToAdd;
      }
    } else {
      // Första raden är ogiltig - skapa fallback
      const firstTemplateSection = template?.sections?.[0];
      const fallbackTitle = firstTemplateSection?.section_title || 'Okänt';
      const textToAdd = [title, content].filter(Boolean).join('\n');
      cleaned.push({ title: fallbackTitle, content: textToAdd });
    }
  });

  return cleaned;
}

/**
 * Extraherar innehåll för en specifik sektion från fulltext
 * @param {string} fullText - Hela texten med alla sektioner
 * @param {string} sectionTitle - Rubriken för sektionen att extrahera
 * @returns {string} Innehållet för den specifika sektionen
 */
export function extractSectionContent(fullText, sectionTitle) {
  // Escapa specialtecken så rubriker med ( ) / ? etc. inte sabbar regexen
  const safeTitle = sectionTitle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  /* 1) Dela upp hela texten i sektioner.
   *   – Vi letar efter rader som börjar med stor bokstav (inkl. ÅÄÖ) och slutar med kolon.
   *   – Snedstreck och mellanslag är tillåtna i rubriken.
   */
  const splitter = new RegExp(
    `^(${safeTitle}|[A-ZÅÄÖ][A-Za-zÅÄÖåäö/ ]{1,50}):\\s*`,
    "mi"
  );

  const parts = fullText.split(splitter);

  /**
   * split() returnerar en array:
   *   [  <text före första rubriken>,
   *      <match1>, <text1>,
   *      <match2>, <text2>, … ]
   * Vi går igenom två index åt gången (rubrik + innehåll).
   */
  for (let i = 1; i < parts.length; i += 2) {
    const currentTitle = parts[i].trim().replace(/:$/, "");
    const content = parts[i + 1] ?? "";
    if (currentTitle.toLowerCase() === sectionTitle.trim().toLowerCase()) {
      return content.trim();
    }
  }
  // Om rubriken inte hittas
  return "";
}

/**
 * Förbereder sektioner för GPT-generering - både sanerar och sorterar
 * @param {Array} sections - Sektioner att förbereda
 * @param {Object} template - Mall att använda
 * @returns {Array} Förberedda sektioner
 */
export function prepareSections(sections, template) {
  return applyOrderFromTemplate(
    sanitizeGptSections(sections, template),
    template
  );
}